import { useApp, useFetch } from '../store.tsx';
import { Card, Spinner } from '../components.tsx';

/**
 * What has reached the server, and what became of it.
 *
 * This exists because "I set up the Shortcut and nothing shows up" was only
 * answerable from the server logs — which the person with the problem cannot
 * read, and which nobody can read on someone else's behalf. Handing a friend
 * the app meant handing them a dead end.
 *
 * The distinction it is built to make obvious is between *nothing arriving* and
 * *things arriving and failing*. They look identical on the Home screen and
 * have completely different fixes: the first is a Shortcut that is not firing
 * or is posting with the wrong key, the second is a message the parser does not
 * recognise.
 */

interface IngestEvent {
  id: string;
  receivedAt: string;
  sourceType: string;
  status: string;
  reason: string | null;
  sender: string | null;
  transactionId: string | null;
  preview: string | null;
}

/** Plain language for a database status. The reason codes are not for users. */
function describe(e: IngestEvent, ar: boolean): { label: string; tone: 'ok' | 'warn' | 'bad'; detail: string } {
  switch (e.status) {
    case 'processed':
      return {
        label: ar ? 'أُضيفت' : 'Added',
        tone: 'ok',
        detail: ar ? 'ظهرت كحركة.' : 'Became a transaction.',
      };
    case 'duplicate':
      return {
        label: ar ? 'مكررة' : 'Duplicate',
        tone: 'ok',
        detail: ar
          ? 'وصلت هذه الرسالة من قبل، فلم تُحتسب مرتين.'
          : 'This message had already arrived, so it was not counted twice.',
      };
    case 'ignored':
      return {
        label: ar ? 'ليست حركة' : 'Not a transaction',
        tone: 'warn',
        detail: ar
          ? 'رسالة بنكية لكنها ليست عملية شراء — مثل رمز تحقق أو عرض ترويجي.'
          : 'A bank message, but not a purchase — an OTP or a marketing message, for example.',
      };
    case 'needs_review':
      return {
        label: ar ? 'تحتاج مراجعة' : 'Needs review',
        tone: 'warn',
        detail: ar
          ? 'قُرئت جزئياً. راجعها في الحركات.'
          : 'Partly read. Check it under Activity.',
      };
    case 'pending':
      return {
        label: ar ? 'قيد المعالجة' : 'Processing',
        tone: 'warn',
        detail: ar ? 'وصلت للتو.' : 'Just arrived.',
      };
    default:
      return {
        label: ar ? 'تعذّرت القراءة' : 'Could not be read',
        tone: 'bad',
        detail: ar
          ? 'وصلت الرسالة لكن تعذّر استخراج مبلغ وتاجر منها. صيغة هذا البنك غير مدعومة بعد.'
          : 'It arrived, but no amount and merchant could be extracted. This bank’s wording is not recognised yet.',
      };
  }
}

const TONE_COLOR: Record<string, string> = {
  ok: 'var(--positive, var(--accent))',
  warn: 'var(--warning)',
  bad: 'var(--negative)',
};

export function IngestActivity(): JSX.Element {
  const { locale } = useApp();
  const ar = locale === 'ar';
  const { data, loading } = useFetch<{ events: IngestEvent[] }>('/ingest/recent?limit=15');

  if (loading) return <Spinner />;

  const events = data?.events ?? [];

  // The empty case is the most informative one on this screen, so it says what
  // to go and check rather than just "nothing here".
  if (!events.length) {
    return (
      <Card style={{ borderColor: 'var(--warning)' }}>
        <div className="small strong">{ar ? 'لم تصل أي رسالة بعد' : 'No messages have arrived yet'}</div>
        <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
          {ar
            ? 'لم يستلم الخادم شيئاً من هذا الحساب. الأسباب المعتادة: الاختصار لم يُشغّل بعد، أو "الاستقبال من" مضبوط على "لا مكان"، أو المفتاح المستخدم يخص حساباً آخر — كل حساب له مفتاحه الخاص.'
            : 'The server has received nothing at all for this account. The usual causes: the Shortcut has not run yet, its “Receive” is set to Nowhere, or it is using a key that belongs to a different account — every account has its own key.'}
        </div>
      </Card>
    );
  }

  const unreadable = events.filter((e) => e.status === 'failed').length;

  return (
    <div className="stack" style={{ gap: 10 }}>
      {unreadable > 0 && unreadable === events.length && (
        <Card style={{ borderColor: 'var(--negative)' }}>
          <div className="small strong">
            {ar ? 'الرسائل تصل لكن لا تُقرأ' : 'Messages are arriving but cannot be read'}
          </div>
          <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
            {ar
              ? 'الاختصار يعمل بشكل صحيح — المشكلة في صيغة رسائل هذا البنك. أرسل نص إحدى الرسائل إلى الدعم ليُضاف دعمها.'
              : 'The Shortcut is working correctly — the problem is this bank’s message format. Send one of these to support and it can be added.'}
          </div>
        </Card>
      )}

      {events.map((e) => {
        const d = describe(e, ar);
        return (
          <Card key={e.id}>
            <div className="row row--between" style={{ gap: 10 }}>
              <span className="grow">
                <span className="small strong" style={{ display: 'block', color: TONE_COLOR[d.tone] }}>
                  {d.label}
                </span>
                <span className="tiny muted" style={{ display: 'block', marginTop: 2 }}>
                  {new Date(e.receivedAt).toLocaleString()}
                  {e.sender && ` · ${e.sender}`}
                </span>
              </span>
            </div>
            <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.55 }}>{d.detail}</div>
            {e.preview && (
              <div
                className="tiny mono"
                dir="auto"
                style={{
                  marginTop: 8, padding: '8px 10px', borderRadius: 8,
                  background: 'var(--bg-sunken)', color: 'var(--text-secondary)',
                  lineHeight: 1.5, wordBreak: 'break-word',
                }}
              >
                {e.preview}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
